import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useReviews } from '../../context/ReviewContext';
import { Star } from 'lucide-react';
import logger from '../../utils/logging';
import SubscriberChat from '../../components/features/SubscriberChat';

const CustomerProfile = () => {
  const { user, logout, updateUser, getCustomerOrders } = useAuth();
  const { getProductReviews, getUnreviewedPurchasedProducts, addReview } = useReviews();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [reviewForm, setReviewForm] = useState({
    rating: 5,
    title: '',
    comment: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const isZee = user?.email ? ['chris@rainbow-sails.com', 'zee@rainbow-sails.com'].includes(user.email.toLowerCase()) : false;
  const canvasRef = useRef(null);

  useEffect(() => {
    logger.logComponentMount('CustomerProfile', { 
      hasUser: !!user,
      userId: user?.id 
    });

    if (!user) {
      navigate('/auth');
      return;
    }

    // Load customer orders
    const customerOrders = getCustomerOrders();
    setOrders(customerOrders);
    
    // Load unreviewed products for review tab
    const unreviewedProducts = getUnreviewedPurchasedProducts();
    console.log('📝 Unreviewed products:', unreviewedProducts);

    // Initialize edit data
    setEditData({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email || '',
      phone: user.phone || '',
      address: user.address?.street || '',
      city: user.address?.city || '',
      state: user.address?.state || '',
      postcode: user.address?.postcode || '',
      country: user.address?.country || 'Australia'
    });
  }, [user, navigate, getCustomerOrders, getUnreviewedPurchasedProducts]);

  // Matrix Rain Effect
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    
    const matrix = "ABCDEFGHIJKLMNOPQRSTUVWXYZ123456789@#$%^&*()*&^%+-/~{[|`]}";
    const matrixArray = matrix.split("");
    const fontSize = 10;
    const columns = canvas.width / fontSize;
    const drops = [];
    
    for (let x = 0; x < columns; x++) {
      drops[x] = 1;
    }
    
    let hue = 0;
    let bgHue = 180;
    
    function draw() {
      ctx.fillStyle = `hsla(${bgHue}, 70%, 5%, 0.1)`;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      bgHue = (bgHue + 0.5) % 360;
      
      for (let i = 0; i < drops.length; i++) {
        const text = matrixArray[Math.floor(Math.random() * matrixArray.length)];
        const columnHue = (hue + i * 10) % 360;
        ctx.fillStyle = `hsl(${columnHue}, 100%, 60%)`;
        ctx.font = fontSize + 'px monospace';
        ctx.fillText(text, i * fontSize, drops[i] * fontSize);
        
        if (drops[i] * fontSize > canvas.height && Math.random() > 0.975) {
          drops[i] = 0;
        }
        drops[i]++;
      }
      
      hue = (hue + 2) % 360;
    }
    
    const interval = setInterval(draw, 35);
    
    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    logger.logAuthAction('logout');
    logout();
    navigate('/');
  };

  const handleSave = () => {
    try {
      updateUser({
        firstName: editData.firstName,
        lastName: editData.lastName,
        phone: editData.phone,
        address: {
          street: editData.address,
          city: editData.city,
          state: editData.state,
          postcode: editData.postcode,
          country: editData.country
        }
      });
      logger.logButtonClick('Profile Save', { userId: user.id });
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update profile:', error);
    }
  };

  // Get all products the user has reviewed
  const getUserReviews = () => {
    try {
      const allReviews = [];
      orders.forEach(order => {
        order.items.forEach(item => {
          if (item.productId) {
            const productReviews = getProductReviews(item.productId);
            const userReviews = productReviews.filter(review => review.userId === user.id);
            userReviews.forEach(review => {
              if (!allReviews.find(r => r.id === review.id)) {
                allReviews.push({
                  ...review,
                  productName: item.title,
                  productImage: item.image
                });
              }
            });
          }
        });
      });
      return allReviews.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    } catch (error) {
      console.error('Error getting user reviews:', error);
      return [];
    }
  };

  const userReviews = getUserReviews();
  const unreviewedProducts = getUnreviewedPurchasedProducts();

  const loadSubscription = useCallback(async () => {
    if (!user?.id) return;
    try {
      const baseUrl = process.env.REACT_APP_API_URL || 'http://168.231.118.116:3001';
      const response = await fetch(`${baseUrl}/api/subscriptions/${user.id}`);
      const data = await response.json();
      if (data.success) {
        setSubscription(data.subscription);
      }
    } catch (error) {
      console.error('Error loading subscription:', error);
    }
  }, [user?.id]);

  // Load subscription data
  useEffect(() => {
    loadSubscription();
  }, [loadSubscription]);

  // Handle review submission
  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!selectedProduct) return;
    
    setSubmitting(true);
    try {
      await addReview({
        productId: selectedProduct.id,
        productName: selectedProduct.name,
        ...reviewForm
      });
      
      setReviewForm({ rating: 5, title: '', comment: '' });
      setShowReviewForm(false);
      setSelectedProduct(null);
      alert('✅ Review submitted successfully!');
    } catch (error) {
      alert(`❌ ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };
  
  // Render stars for rating
  const renderStars = (rating, size = 20, interactive = false, onChange = null) => {
    return (
      <div style={{ display: 'flex', gap: '4px' }}>
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            size={size}
            fill={star <= rating ? '#fbbf24' : 'none'}
            color={star <= rating ? '#fbbf24' : '#6b7280'}
            style={{
              cursor: interactive ? 'pointer' : 'default',
              transition: 'all 0.2s ease'
            }}
            onClick={() => {
              if (interactive && onChange) {
                onChange(star);
              }
            }}
          />
        ))}
      </div>
    );
  };

  if (!user) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'radial-gradient(ellipse at center, #1a1a2e 0%, #16213e 50%, #0f0f23 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        paddingTop: '80px'
      }}>
        <div style={{ textAlign: 'center' }}>
          <p style={{ fontSize: '18px', marginBottom: '16px' }}>Please log in to view your profile</p>
          <button
            onClick={() => navigate('/auth')}
            style={{
              background: 'linear-gradient(135deg, #ec4899, #8b5cf6)',
              color: 'white',
              padding: '12px 24px',
              borderRadius: '8px',
              border: 'none',
              cursor: 'pointer'
            }}
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  const handleToggleAutoRenew = async () => {
    if (!subscription) return;
    
    setSubscriptionLoading(true);
    try {
      const newAutoRenew = !subscription.autoRenew;
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://168.231.118.116:3001'}/api/subscriptions/${subscription.id}/toggle-auto-renew`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ autoRenew: newAutoRenew }),
      });

      const data = await response.json();
      if (data.success) {
        setSubscription(data.subscription);
        alert(data.message);
      } else {
        alert(data.error || 'Failed to update subscription');
      }
    } catch (error) {
      console.error('Error toggling auto-renew:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!subscription) return;
    
    if (!window.confirm('Are you sure you want to cancel your subscription? This action cannot be undone.')) {
      return;
    }

    setSubscriptionLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://168.231.118.116:3001'}/api/subscriptions/${subscription.id}/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();
      if (data.success) {
        setSubscription(null);
        alert(data.message);
      } else {
        alert(data.error || 'Failed to cancel subscription');
      }
    } catch (error) {
      console.error('Error canceling subscription:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: '📊' },
    { id: 'account', label: 'Account Details', icon: '👤' },
    { id: 'subscription', label: 'Subscription', icon: '🎁' },
    { id: 'orders', label: 'Orders', icon: '📦' },
    { id: 'reviews', label: 'Reviews', icon: '⭐' }
  ];

  return (
    <div style={{
      minHeight: '100vh',
      position: 'relative',
      background: 'radial-gradient(ellipse at center, #1a1a2e 0%, #16213e 50%, #0f0f23 100%)',
      overflow: 'auto'
    }}>
      {/* Matrix Rain */}
      <canvas 
        ref={canvasRef}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 0,
          pointerEvents: 'none'
        }}
      />

      {/* Floating Particles */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 1,
        pointerEvents: 'none'
      }}>
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            style={{
              position: 'absolute',
              width: '4px',
              height: '4px',
              background: `hsl(${(i * 18) % 360}, 70%, 60%)`,
              borderRadius: '50%',
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animation: `float ${3 + Math.random() * 4}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 2}s`,
              opacity: 0.6
            }}
          />
        ))}
      </div>

      {/* Morphing Shapes */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 1,
        pointerEvents: 'none'
      }}>
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            style={{
              position: 'absolute',
              left: `${20 + i * 15}%`,
              top: `${30 + i * 10}%`,
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(45deg, #ff6b6b, #4ecdc4)',
              animation: `morph 8s ease-in-out infinite`,
              animationDelay: `${i * 1.5}s`,
              opacity: 0.3
            }}
          />
        ))}
      </div>

      <div style={{
        position: 'relative',
        zIndex: 10,
        color: 'white',
        padding: window.innerWidth <= 768 ? '80px 16px 24px 16px' : '80px 24px 24px 24px',
        width: '100%',
        boxSizing: 'border-box',
        overflowX: 'hidden'
      }}>
        <style>{`
          @keyframes float {
            0%, 100% { transform: translateY(0px) rotate(0deg); }
            25% { transform: translateY(-20px) rotate(90deg); }
            50% { transform: translateY(-10px) rotate(180deg); }
            75% { transform: translateY(-30px) rotate(270deg); }
          }
          
          @keyframes morph {
            0%, 100% { 
              transform: scale(1) rotate(0deg);
              border-radius: 50%;
            }
            25% { 
              transform: scale(1.2) rotate(90deg);
              border-radius: 30% 70% 70% 30% / 30% 30% 70% 70%;
            }
            50% { 
              transform: scale(0.8) rotate(180deg);
              border-radius: 70% 30% 30% 70% / 70% 70% 30% 30%;
            }
            75% { 
              transform: scale(1.1) rotate(270deg);
              border-radius: 40% 60% 60% 40% / 60% 40% 60% 40%;
            }
          }
        `}</style>
        <div style={{
          maxWidth: '1200px',
          width: '100%',
          boxSizing: 'border-box',
          padding: window.innerWidth <= 768 ? '0 16px' : '0 24px',
          margin: '0 auto'
        }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '32px'
        }}>
          <div>
            <h1 style={{
              fontSize: '2.5rem',
              fontWeight: 'bold',
              background: 'linear-gradient(45deg, #ec4899, #8b5cf6, #06b6d4)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              marginBottom: '8px'
            }}>
              My Profile
            </h1>
            <p style={{
              color: '#9ca3af',
              fontSize: '18px'
            }}>
              Welcome back, {user.firstName}! Manage your account and orders.
            </p>
          </div>
          <button
            onClick={handleLogout}
            style={{
              background: 'transparent',
              color: '#ef4444',
              border: '1px solid #ef4444',
              padding: '8px 16px',
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = '#ef4444';
              e.target.style.color = 'white';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'transparent';
              e.target.style.color = '#ef4444';
            }}
          >
            Logout
          </button>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginBottom: '32px',
          background: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(20px)',
          borderRadius: '16px',
          padding: '8px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
        }}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => {
                logger.logButtonClick(`Profile Tab: ${tab.label}`);
                setActiveTab(tab.id);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 24px',
                borderRadius: '12px',
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                fontWeight: '500',
                fontSize: '16px',
                background: activeTab === tab.id ? 'linear-gradient(135deg, #ec4899, #8b5cf6)' : 'transparent',
                color: activeTab === tab.id ? 'white' : '#d1d5db',
                boxShadow: activeTab === tab.id ? '0 4px 15px rgba(236, 72, 153, 0.4)' : 'none',
                transform: activeTab === tab.id ? 'scale(1.05)' : 'scale(1)'
              }}
              onMouseEnter={(e) => {
                if (activeTab !== tab.id) {
                  e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                  e.target.style.color = 'white';
                  e.target.style.transform = 'scale(1.02)';
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== tab.id) {
                  e.target.style.background = 'transparent';
                  e.target.style.color = '#d1d5db';
                  e.target.style.transform = 'scale(1)';
                }
              }}
            >
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div style={{
          background: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(20px)',
          borderRadius: '24px',
          padding: '32px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.4)',
          minHeight: '500px'
        }}>
          {activeTab === 'dashboard' && (
            <div>
              <h2 style={{
                fontSize: '28px',
                fontWeight: 'bold',
                color: 'white',
                marginBottom: '32px',
                textAlign: 'center',
                textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
              }}>
                Dashboard Overview
              </h2>

              {/* Statistics Cards */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '16px',
                marginBottom: '32px'
              }}>
                <div style={{
                  background: 'rgba(0, 0, 0, 0.6)',
                  padding: '20px',
                  borderRadius: '12px',
                  border: '1px solid rgba(16, 185, 129, 0.3)',
                  textAlign: 'center'
                }}>
                  <h3 style={{ color: '#10b981', fontSize: '2rem', fontWeight: 'bold', margin: '0 0 8px 0' }}>
                    {orders.length}
                  </h3>
                  <p style={{ color: '#9ca3af', fontSize: '14px', margin: 0 }}>
                    Total Orders
                  </p>
                </div>
                <div style={{
                  background: 'rgba(0, 0, 0, 0.6)',
                  padding: '20px',
                  borderRadius: '12px',
                  border: '1px solid rgba(139, 92, 246, 0.3)',
                  textAlign: 'center'
                }}>
                  <h3 style={{ color: '#8b5cf6', fontSize: '2rem', fontWeight: 'bold', margin: '0 0 8px 0' }}>
                    {userReviews.length}
                  </h3>
                  <p style={{ color: '#9ca3af', fontSize: '14px', margin: 0 }}>
                    Your Reviews
                  </p>
                </div>
                <div style={{
                  background: 'rgba(0, 0, 0, 0.6)',
                  padding: '20px',
                  borderRadius: '12px',
                  border: '1px solid rgba(236, 72, 153, 0.3)',
                  textAlign: 'center'
                }}>
                  <h3 style={{ color: '#ec4899', fontSize: '2rem', fontWeight: 'bold', margin: '0 0 8px 0' }}>
                    {orders.filter(order => order.status === 'completed').length}
                  </h3>
                  <p style={{ color: '#9ca3af', fontSize: '14px', margin: 0 }}>
                    Completed
                  </p>
                </div>
              </div>

              {/* Recent Reviews */}
              {userReviews.length > 0 && (
                <div style={{
                  background: 'rgba(139, 92, 246, 0.1)',
                  border: '1px solid rgba(139, 92, 246, 0.3)',
                  borderRadius: '12px',
                  padding: '24px',
                  marginBottom: '32px'
                }}>
                  <h3 style={{
                    color: '#8b5cf6',
                    fontSize: '20px',
                    fontWeight: 'bold',
                    marginBottom: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    📝 Your Recent Reviews
                  </h3>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                    maxHeight: '300px',
                    overflowY: 'auto'
                  }}>
                    {userReviews.slice(0, 5).map((review) => (
                      <div key={review.id} style={{
                        background: 'rgba(0, 0, 0, 0.4)',
                        padding: '16px',
                        borderRadius: '8px',
                        border: '1px solid rgba(139, 92, 246, 0.2)'
                      }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          marginBottom: '8px'
                        }}>
                          <div>
                            <h4 style={{
                              color: 'white',
                              fontSize: '14px',
                              fontWeight: 'bold',
                              margin: '0 0 4px 0'
                            }}>
                              {review.title}
                            </h4>
                            <p style={{
                              color: '#9ca3af',
                              fontSize: '12px',
                              margin: '0 0 4px 0'
                            }}>
                              {review.productName}
                            </p>
                          </div>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}>
                            {[...Array(5)].map((_, i) => (
                              <span
                                key={i}
                                style={{
                                  color: i < review.rating ? '#fbbf24' : '#6b7280',
                                  fontSize: '12px'
                                }}
                              >
                                ★
                              </span>
                            ))}
                            <span style={{
                              color: '#fbbf24',
                              fontSize: '12px',
                              fontWeight: 'bold',
                              marginLeft: '4px'
                            }}>
                              {review.rating}/5
                            </span>
                          </div>
                        </div>
                        <p style={{
                          color: '#d1d5db',
                          fontSize: '12px',
                          lineHeight: '1.4',
                          margin: '0 0 8px 0',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}>
                          {review.comment}
                        </p>
                        <p style={{
                          color: '#6b7280',
                          fontSize: '10px',
                          margin: 0,
                          fontStyle: 'italic'
                        }}>
                          {new Date(review.createdAt).toLocaleDateString('en-AU', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'account' && (
            <div>
              <h2 style={{
                fontSize: '28px',
                fontWeight: 'bold',
                color: 'white',
                marginBottom: '32px',
                textAlign: 'center',
                textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
              }}>
                Account Details
              </h2>

              {isEditing ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <input
                      type="text"
                      placeholder="First Name"
                      value={editData.firstName}
                      onChange={(e) => setEditData({...editData, firstName: e.target.value})}
                      style={{
                        padding: '8px 12px',
                        background: 'rgba(255, 255, 255, 0.1)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '6px',
                        color: 'white',
                        outline: 'none'
                      }}
                    />
                    <input
                      type="text"
                      placeholder="Last Name"
                      value={editData.lastName}
                      onChange={(e) => setEditData({...editData, lastName: e.target.value})}
                      style={{
                        padding: '8px 12px',
                        background: 'rgba(255, 255, 255, 0.1)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '6px',
                        color: 'white',
                        outline: 'none'
                      }}
                    />
                  </div>
                  <input
                    type="email"
                    placeholder="Email"
                    value={editData.email}
                    disabled={true}
                    style={{
                      padding: '8px 12px',
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '6px',
                      color: '#9ca3af',
                      outline: 'none'
                    }}
                  />
                  <input
                    type="tel"
                    placeholder="Phone"
                    value={editData.phone}
                    onChange={(e) => setEditData({...editData, phone: e.target.value})}
                    style={{
                      padding: '8px 12px',
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '6px',
                      color: 'white',
                      outline: 'none'
                    }}
                  />
                  <input
                    type="text"
                    placeholder="Address"
                    value={editData.address}
                    onChange={(e) => setEditData({...editData, address: e.target.value})}
                    style={{
                      padding: '8px 12px',
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '6px',
                      color: 'white',
                      outline: 'none'
                    }}
                  />
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                    <input
                      type="text"
                      placeholder="City"
                      value={editData.city}
                      onChange={(e) => setEditData({...editData, city: e.target.value})}
                      style={{
                        padding: '8px 12px',
                        background: 'rgba(255, 255, 255, 0.1)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '6px',
                        color: 'white',
                        outline: 'none'
                      }}
                    />
                    <input
                      type="text"
                      placeholder="State"
                      value={editData.state}
                      onChange={(e) => setEditData({...editData, state: e.target.value})}
                      style={{
                        padding: '8px 12px',
                        background: 'rgba(255, 255, 255, 0.1)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '6px',
                        color: 'white',
                        outline: 'none'
                      }}
                    />
                    <input
                      type="text"
                      placeholder="Postcode"
                      value={editData.postcode}
                      onChange={(e) => setEditData({...editData, postcode: e.target.value})}
                      style={{
                        padding: '8px 12px',
                        background: 'rgba(255, 255, 255, 0.1)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '6px',
                        color: 'white',
                        outline: 'none'
                      }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                    <button
                      onClick={handleSave}
                      style={{
                        background: 'linear-gradient(135deg, #10b981, #059669)',
                        color: 'white',
                        padding: '10px 20px',
                        borderRadius: '8px',
                        border: 'none',
                        cursor: 'pointer',
                        flex: 1
                      }}
                    >
                      Save Changes
                    </button>
                    <button
                      onClick={() => setIsEditing(false)}
                      style={{
                        background: 'transparent',
                        color: '#9ca3af',
                        border: '1px solid #374151',
                        padding: '10px 20px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        flex: 1
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div>
                      <span style={{ color: '#9ca3af', fontSize: '14px' }}>First Name</span>
                      <p style={{ color: 'white', fontSize: '16px', margin: '4px 0' }}>{user.firstName}</p>
                    </div>
                    <div>
                      <span style={{ color: '#9ca3af', fontSize: '14px' }}>Last Name</span>
                      <p style={{ color: 'white', fontSize: '16px', margin: '4px 0' }}>{user.lastName}</p>
                    </div>
                  </div>
                  <div>
                    <span style={{ color: '#9ca3af', fontSize: '14px' }}>Email</span>
                    <p style={{ color: 'white', fontSize: '16px', margin: '4px 0' }}>{user.email}</p>
                  </div>
                  <div>
                    <span style={{ color: '#9ca3af', fontSize: '14px' }}>Phone</span>
                    <p style={{ color: 'white', fontSize: '16px', margin: '4px 0' }}>{user.phone || 'Not provided'}</p>
                  </div>
                  <div>
                    <span style={{ color: '#9ca3af', fontSize: '14px' }}>Address</span>
                    <p style={{ color: 'white', fontSize: '16px', margin: '4px 0' }}>
                      {user.address?.street ? `${user.address.street}, ${user.address.city}, ${user.address.state} ${user.address.postcode}` : 'Not provided'}
                    </p>
                  </div>
                  <button
                    onClick={() => setIsEditing(true)}
                    style={{
                      background: 'linear-gradient(135deg, #ec4899, #8b5cf6)',
                      color: 'white',
                      padding: '10px 20px',
                      borderRadius: '8px',
                      border: 'none',
                      cursor: 'pointer',
                      marginTop: '16px'
                    }}
                  >
                    Edit Profile
                  </button>
                </div>
              )}
            </div>
          )}

          {activeTab === 'subscription' && (
            <div>
              <h2 style={{
                fontSize: '28px',
                fontWeight: 'bold',
                color: 'white',
                marginBottom: '32px',
                textAlign: 'center',
                textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
              }}>
                Zee's Mystery Subscription Test
              </h2>

              {subscription ? (
                <div style={{
                  background: 'rgba(139, 92, 246, 0.1)',
                  border: '1px solid rgba(139, 92, 246, 0.3)',
                  borderRadius: '12px',
                  padding: '24px',
                  marginBottom: '24px'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '20px'
                  }}>
                    <img
                      src="/favicon.png"
                      alt="Rainbow Sails"
                      style={{
                        width: '60px',
                        height: '60px',
                        objectFit: 'contain'
                      }}
                      onError={(e) => {
                        e.target.src = '/Logo.png';
                      }}
                    />
                    <div>
                      <h3 style={{
                        color: '#c4b5fd',
                        fontSize: '20px',
                        fontWeight: 'bold',
                        margin: '0 0 4px 0'
                      }}>
                        Zee Mystery Subscription Test
                      </h3>
                      <p style={{
                        color: '#9ca3af',
                        fontSize: '14px',
                        margin: 0
                      }}>
                        $1.00 AUD billed monthly while Zee runs the mystery subscription test
                      </p>
                    </div>
                  </div>

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '16px',
                    marginBottom: '24px'
                  }}>
                    <div>
                      <span style={{ color: '#9ca3af', fontSize: '14px', display: 'block', marginBottom: '4px' }}>
                        Status
                      </span>
                      <span style={{
                        color: subscription.status === 'active' ? '#10b981' : '#f59e0b',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        textTransform: 'capitalize'
                      }}>
                        {subscription.status}
                      </span>
                    </div>
                    <div>
                      <span style={{ color: '#9ca3af', fontSize: '14px', display: 'block', marginBottom: '4px' }}>
                        Auto-Renew
                      </span>
                      <span style={{
                        color: subscription.autoRenew ? '#10b981' : '#f59e0b',
                        fontSize: '16px',
                        fontWeight: 'bold'
                      }}>
                        {subscription.autoRenew ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                    <div>
                      <span style={{ color: '#9ca3af', fontSize: '14px', display: 'block', marginBottom: '4px' }}>
                        Next Billing Date
                      </span>
                      <span style={{
                        color: 'white',
                        fontSize: '16px',
                        fontWeight: '500'
                      }}>
                        {new Date(subscription.currentPeriodEnd).toLocaleDateString('en-AU', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </span>
                    </div>
                  </div>

                  <div style={{
                    background: 'rgba(59, 130, 246, 0.12)',
                    border: '1px solid rgba(59, 130, 246, 0.35)',
                    borderRadius: '10px',
                    padding: '16px',
                    marginBottom: '16px'
                  }}>
                    <p style={{
                      color: '#bfdbfe',
                      fontSize: '14px',
                      lineHeight: '1.6',
                      margin: '0 0 6px 0'
                    }}>
                      ℹ️ Zee\'s tip: auto-renew begins off. If you want this $1 monthly mystery subscription test to continue, stay in Profile > Subscription and tap "Enable Auto-Renew" whenever you are ready.
                    </p>
                    <p style={{
                      color: '#cbd5f5',
                      fontSize: '13px',
                      lineHeight: '1.6',
                      margin: 0
                    }}>
                      Everyone helping Zee run this test will be first to receive the mystery drop when the experiment concludes.
                    </p>
                  </div>

                  {!subscription.autoRenew && (
                    <div style={{
                      background: 'rgba(245, 158, 11, 0.1)',
                      border: '1px solid rgba(245, 158, 11, 0.3)',
                      borderRadius: '8px',
                      padding: '12px',
                      marginBottom: '16px'
                    }}>
                      <p style={{
                        color: '#fbbf24',
                        fontSize: '14px',
                        margin: 0,
                        textAlign: 'center'
                      }}>
                      ⚠️ Auto-renew is currently disabled. Zee recommends tapping "Enable Auto-Renew" below before {new Date(subscription.currentPeriodEnd).toLocaleDateString('en-AU', { month: 'long', day: 'numeric' })} if you want to stay in the $1 mystery subscription test through the final drop.
                      </p>
                    </div>
                  )}

                  <div style={{
                    display: 'flex',
                    gap: '12px',
                    flexWrap: 'wrap'
                  }}>
                    <button
                      onClick={handleToggleAutoRenew}
                      disabled={subscriptionLoading}
                      style={{
                        flex: 1,
                        minWidth: '200px',
                        background: subscription.autoRenew 
                          ? 'linear-gradient(135deg, #f59e0b, #d97706)' 
                          : 'linear-gradient(135deg, #10b981, #059669)',
                        color: 'white',
                        padding: '12px 24px',
                        borderRadius: '8px',
                        border: 'none',
                        fontSize: '16px',
                        fontWeight: '600',
                        cursor: subscriptionLoading ? 'not-allowed' : 'pointer',
                        opacity: subscriptionLoading ? 0.6 : 1,
                        transition: 'all 0.3s ease'
                      }}
                    >
                      {subscriptionLoading 
                        ? 'Processing...' 
                        : subscription.autoRenew 
                          ? 'Disable Auto-Renew' 
                          : 'Enable Auto-Renew'}
                    </button>
                    <button
                      onClick={handleCancelSubscription}
                      disabled={subscriptionLoading}
                      style={{
                        flex: 1,
                        minWidth: '200px',
                        background: 'transparent',
                        color: '#ef4444',
                        border: '1px solid #ef4444',
                        padding: '12px 24px',
                        borderRadius: '8px',
                        fontSize: '16px',
                        fontWeight: '600',
                        cursor: subscriptionLoading ? 'not-allowed' : 'pointer',
                        opacity: subscriptionLoading ? 0.6 : 1,
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (!subscriptionLoading) {
                          e.target.style.background = '#ef4444';
                          e.target.style.color = 'white';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!subscriptionLoading) {
                          e.target.style.background = 'transparent';
                          e.target.style.color = '#ef4444';
                        }
                      }}
                    >
                      Cancel Subscription
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{
                  textAlign: 'center',
                  padding: '48px 0',
                  color: '#9ca3af'
                }}>
                  <div style={{ fontSize: '4rem', marginBottom: '16px' }}>🎁</div>
                  <h3 style={{
                    fontSize: '1.5rem',
                    fontWeight: '600',
                    marginBottom: '8px',
                    color: '#d1d5db'
                  }}>
                    No Active Subscription
                  </h3>
                  <p style={{
                    fontSize: '1rem',
                    marginBottom: '24px',
                    maxWidth: '500px',
                    margin: '0 auto 24px auto'
                  }}>
                    Zee is running a $1 mystery subscription test. After you purchase, head to Profile > Subscription and switch on auto-renew if you want the surprises to keep coming. Drops arrive whenever Zee is ready to share them, and every supporter gets first dibs on the final reveal.
                  </p>
                  <button
                    onClick={() => navigate('/product/mystery-test-subscription')}
                    style={{
                      background: 'linear-gradient(135deg, #8b5cf6, #ec4899)',
                      color: 'white',
                      padding: '12px 24px',
                      borderRadius: '8px',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '16px',
                      fontWeight: '500'
                    }}
                  >
                    Subscribe Now
                  </button>
                </div>
              )}

              {((subscription && subscription.status === 'active') || isZee) && (
                <div style={{ marginTop: '24px' }}>
                  <SubscriberChat />
                </div>
              )}
            </div>
          )}

          {activeTab === 'orders' && (
            <div>
              <h2 style={{
                fontSize: '28px',
                fontWeight: 'bold',
                color: 'white',
                marginBottom: '32px',
                textAlign: 'center',
                textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
              }}>
                Order History
              </h2>

              {orders.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {orders.map((order, index) => (
                    <div key={order.id} style={{
                      background: 'rgba(31, 41, 55, 0.8)',
                      backdropFilter: 'blur(10px)',
                      padding: '20px',
                      borderRadius: '12px',
                      border: '2px solid rgba(16, 185, 129, 0.3)',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                    }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '16px'
                      }}>
                        <div>
                          <h4 style={{ color: 'white', fontWeight: 'bold', fontSize: '18px', margin: 0 }}>
                            Order #{order.id}
                          </h4>
                          <p style={{ color: '#9ca3af', fontSize: '14px', margin: '4px 0 0 0' }}>
                            {new Date(order.date).toLocaleDateString()} • {order.customerName}
                          </p>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{
                            background: order.status === 'completed' ? '#10b981' : '#f59e0b',
                            color: 'white',
                            padding: '6px 12px',
                            borderRadius: '20px',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            textTransform: 'uppercase',
                            marginBottom: '8px'
                          }}>
                            {order.status}
                          </div>
                          <p style={{ color: '#10b981', fontWeight: 'bold', fontSize: '18px', margin: 0 }}>
                            AUD ${order.total.toFixed(2)}
                          </p>
                        </div>
                      </div>
                      <div style={{ marginBottom: '16px' }}>
                        <h5 style={{ color: 'white', fontSize: '14px', fontWeight: '500', marginBottom: '12px' }}>
                          Items ({order.items.length}):
                        </h5>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {order.items.map((item, itemIndex) => (
                            <div key={itemIndex} style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '12px',
                              background: 'rgba(0, 0, 0, 0.2)',
                              padding: '8px',
                              borderRadius: '8px'
                            }}>
                              {item.image && (
                                <img
                                  src={item.image}
                                  alt={item.name}
                                  style={{
                                    width: '40px',
                                    height: '40px',
                                    borderRadius: '6px',
                                    objectFit: 'cover'
                                  }}
                                />
                              )}
                              <div style={{ flex: 1 }}>
                                <p style={{ color: 'white', fontSize: '14px', margin: 0, fontWeight: '500' }}>
                                  {item.name}
                                </p>
                                {item.variant && (
                                  <p style={{ color: '#9ca3af', fontSize: '12px', margin: 0 }}>
                                    {item.variant}
                                  </p>
                                )}
                              </div>
                              <div style={{ textAlign: 'right' }}>
                                <p style={{ color: 'white', fontSize: '14px', margin: 0 }}>
                                  Qty: {item.quantity}
                                </p>
                                <p style={{ color: '#10b981', fontSize: '14px', margin: 0, fontWeight: '500' }}>
                                  ${item.price}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div style={{ marginBottom: '16px' }}>
                        <p style={{ color: '#9ca3af', fontSize: '14px', margin: 0 }}>
                          Customer: {order.email}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{
                  textAlign: 'center',
                  padding: '48px 0',
                  color: '#9ca3af'
                }}>
                  <div style={{ fontSize: '4rem', marginBottom: '16px' }}>🛒</div>
                  <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '8px', color: '#d1d5db' }}>
                    No orders yet
                  </h3>
                  <p style={{ fontSize: '1rem', marginBottom: '24px' }}>
                    Start your journey with Rainbow Sails
                  </p>
                  <button
                    onClick={() => navigate('/gallery')}
                    style={{
                      background: 'linear-gradient(135deg, #ec4899, #8b5cf6)',
                      color: 'white',
                      padding: '12px 24px',
                      borderRadius: '8px',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '16px',
                      fontWeight: '500'
                    }}
                  >
                    Start Shopping
                  </button>
                </div>
              )}
            </div>
          )}
          
          {activeTab === 'reviews' && (
            <div>
              <h2 style={{
                fontSize: '28px',
                fontWeight: 'bold',
                color: 'white',
                marginBottom: '32px',
                textAlign: 'center',
                textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
              }}>
                Your Reviews
              </h2>
              
              {/* Review Form Modal */}
              {showReviewForm && selectedProduct && (
                <div style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(0, 0, 0, 0.8)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1000,
                  padding: '20px'
                }}>
                  <div style={{
                    background: 'rgba(31, 41, 55, 0.95)',
                    backdropFilter: 'blur(20px)',
                    borderRadius: '16px',
                    padding: '32px',
                    maxWidth: '500px',
                    width: '100%',
                    border: '1px solid rgba(139, 92, 246, 0.3)'
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '24px'
                    }}>
                      <h3 style={{ color: '#8b5cf6', fontSize: '20px', fontWeight: 'bold', margin: 0 }}>
                        Review: {selectedProduct.name}
                      </h3>
                      <button
                        onClick={() => {
                          setShowReviewForm(false);
                          setSelectedProduct(null);
                        }}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: '#9ca3af',
                          fontSize: '24px',
                          cursor: 'pointer'
                        }}
                      >
                        ×
                      </button>
                    </div>
                    
                    <form onSubmit={handleSubmitReview}>
                      <div style={{ marginBottom: '20px' }}>
                        <label style={{ color: 'white', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                          Rating
                        </label>
                        {renderStars(reviewForm.rating, 32, true, (rating) => 
                          setReviewForm({ ...reviewForm, rating })
                        )}
                      </div>

                      <div style={{ marginBottom: '20px' }}>
                        <label style={{ color: 'white', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                          Review Title
                        </label>
                        <input
                          type="text"
                          value={reviewForm.title}
                          onChange={(e) => setReviewForm({ ...reviewForm, title: e.target.value })}
                          placeholder="Sum up your experience..."
                          required
                          style={{
                            width: '100%',
                            padding: '12px',
                            background: 'rgba(255, 255, 255, 0.1)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: '8px',
                            color: 'white',
                            fontSize: '16px',
                            outline: 'none'
                          }}
                        />
                      </div>

                      <div style={{ marginBottom: '24px' }}>
                        <label style={{ color: 'white', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                          Your Review
                        </label>
                        <textarea
                          value={reviewForm.comment}
                          onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                          placeholder="Share your thoughts about this product..."
                          required
                          rows={4}
                          style={{
                            width: '100%',
                            padding: '12px',
                            background: 'rgba(255, 255, 255, 0.1)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: '8px',
                            color: 'white',
                            fontSize: '16px',
                            outline: 'none',
                            resize: 'vertical'
                          }}
                        />
                      </div>

                      <div style={{ display: 'flex', gap: '12px' }}>
                        <button
                          type="submit"
                          disabled={submitting}
                          style={{
                            flex: 1,
                            background: 'linear-gradient(135deg, #8b5cf6, #ec4899)',
                            color: 'white',
                            padding: '12px',
                            borderRadius: '8px',
                            border: 'none',
                            fontSize: '16px',
                            fontWeight: '600',
                            cursor: submitting ? 'not-allowed' : 'pointer',
                            opacity: submitting ? 0.7 : 1
                          }}
                        >
                          {submitting ? 'Submitting...' : 'Submit Review'}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowReviewForm(false);
                            setSelectedProduct(null);
                          }}
                          style={{
                            background: 'transparent',
                            color: '#9ca3af',
                            border: '1px solid #374151',
                            padding: '12px 24px',
                            borderRadius: '8px',
                            cursor: 'pointer'
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}
              
              {/* Products Available for Review */}
              {unreviewedProducts.length > 0 && (
                <div style={{
                  background: 'rgba(16, 185, 129, 0.1)',
                  border: '1px solid rgba(16, 185, 129, 0.3)',
                  borderRadius: '12px',
                  padding: '24px',
                  marginBottom: '32px'
                }}>
                  <h3 style={{
                    color: '#10b981',
                    fontSize: '20px',
                    fontWeight: 'bold',
                    marginBottom: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    📝 Products You Can Review
                  </h3>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                    gap: '16px'
                  }}>
                    {unreviewedProducts.map((product) => (
                      <div key={product.id} style={{
                        background: 'rgba(0, 0, 0, 0.4)',
                        padding: '16px',
                        borderRadius: '8px',
                        border: '1px solid rgba(16, 185, 129, 0.2)'
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          marginBottom: '12px'
                        }}>
                          {product.image && (
                            <img
                              src={product.image}
                              alt={product.name}
                              style={{
                                width: '60px',
                                height: '60px',
                                borderRadius: '8px',
                                objectFit: 'cover'
                              }}
                            />
                          )}
                          <div style={{ flex: 1 }}>
                            <h4 style={{
                              color: 'white',
                              fontSize: '16px',
                              fontWeight: 'bold',
                              margin: '0 0 4px 0'
                            }}>
                              {product.name}
                            </h4>
                            {product.variant && (
                              <p style={{
                                color: '#9ca3af',
                                fontSize: '14px',
                                margin: '0 0 4px 0'
                              }}>
                                {product.variant}
                              </p>
                            )}
                            <p style={{
                              color: '#10b981',
                              fontSize: '12px',
                              margin: 0
                            }}>
                              Order #{product.orderId} • {new Date(product.orderDate).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            setSelectedProduct(product);
                            setShowReviewForm(true);
                          }}
                          style={{
                            width: '100%',
                            background: 'linear-gradient(135deg, #10b981, #059669)',
                            color: 'white',
                            padding: '8px 16px',
                            borderRadius: '6px',
                            border: 'none',
                            fontSize: '14px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = 'linear-gradient(135deg, #059669, #047857)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = 'linear-gradient(135deg, #10b981, #059669)';
                          }}
                        >
                          Write Review
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* User's Existing Reviews */}
              {userReviews.length > 0 ? (
                <div style={{
                  background: 'rgba(139, 92, 246, 0.1)',
                  border: '1px solid rgba(139, 92, 246, 0.3)',
                  borderRadius: '12px',
                  padding: '24px'
                }}>
                  <h3 style={{
                    color: '#8b5cf6',
                    fontSize: '20px',
                    fontWeight: 'bold',
                    marginBottom: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    ⭐ Your Reviews ({userReviews.length})
                  </h3>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px'
                  }}>
                    {userReviews.map((review) => (
                      <div key={review.id} style={{
                        background: 'rgba(0, 0, 0, 0.4)',
                        padding: '20px',
                        borderRadius: '8px',
                        border: '1px solid rgba(139, 92, 246, 0.2)'
                      }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          marginBottom: '12px'
                        }}>
                          <div>
                            <h4 style={{
                              color: 'white',
                              fontSize: '16px',
                              fontWeight: 'bold',
                              margin: '0 0 4px 0'
                            }}>
                              {review.title}
                            </h4>
                            <p style={{
                              color: '#9ca3af',
                              fontSize: '14px',
                              margin: '0 0 4px 0'
                            }}>
                              {review.productName}
                            </p>
                          </div>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            {renderStars(review.rating, 16)}
                            {review.verified && (
                              <span style={{
                                background: '#10b981',
                                color: 'white',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                fontSize: '10px',
                                fontWeight: 'bold'
                              }}>
                                ✓ Verified Purchase
                              </span>
                            )}
                          </div>
                        </div>
                        <p style={{
                          color: '#d1d5db',
                          fontSize: '14px',
                          lineHeight: '1.5',
                          margin: '0 0 12px 0'
                        }}>
                          {review.comment}
                        </p>
                        <p style={{
                          color: '#6b7280',
                          fontSize: '12px',
                          margin: 0,
                          fontStyle: 'italic'
                        }}>
                          {new Date(review.createdAt).toLocaleDateString('en-AU', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : unreviewedProducts.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '48px 0',
                  color: '#9ca3af'
                }}>
                  <div style={{ fontSize: '4rem', marginBottom: '16px' }}>⭐</div>
                  <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '8px', color: '#d1d5db' }}>
                    No reviews yet
                  </h3>
                  <p style={{ fontSize: '1rem', marginBottom: '24px' }}>
                    Purchase products to start reviewing them
                  </p>
                  <button
                    onClick={() => navigate('/gallery')}
                    style={{
                      background: 'linear-gradient(135deg, #ec4899, #8b5cf6)',
                      color: 'white',
                      padding: '12px 24px',
                      borderRadius: '8px',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '16px',
                      fontWeight: '500'
                    }}
                  >
                    Start Shopping
                  </button>
                </div>
              ) : null}
            </div>
          )}
        </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerProfile;